import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Prefix of every awaiting-review count key. Invalidate by prefix from places
 * that change a request's status without knowing whose count it feeds
 * (useFolderOrganize has no profile id) — the counts are head-only and only
 * the mounted building's is live.
 */
export const AWAITING_REVIEW_KEY = "organize-awaiting-review";

export const awaitingReviewKey = (buildingId: number | null, profileId: number | null) => [
  AWAITING_REVIEW_KEY,
  buildingId,
  profileId,
];

/**
 * How many of the current user's organise proposals in this building are
 * waiting for review (WHA-3654). Drives the red dot on the Folders nav item;
 * the dot clears as soon as the user applies or discards (status leaves
 * proposal_ready). OrganizeWatcher invalidates this on every realtime event;
 * the slow poll is only a fallback.
 */
export function useOrganizeAwaitingReview(buildingId: number | null, profileId: number | null) {
  const query = useQuery({
    queryKey: awaitingReviewKey(buildingId, profileId),
    queryFn: async () => {
      const { count, error } = await supabase
        .from("folder_organize_requests")
        .select("id", { count: "exact", head: true })
        .eq("building_id", buildingId as number)
        .eq("requested_by", profileId as number)
        .eq("status", "proposal_ready");
      if (error) throw error;
      return count ?? 0;
    },
    enabled: buildingId !== null && profileId !== null,
    refetchInterval: 60_000,
  });

  return { awaitingReviewCount: query.data ?? 0 };
}
